import { Link2, Megaphone } from 'lucide-preact'
import type { DashboardRange } from '@/types/dashboard'
import type { AnalyticsViewDto, AttributionView } from '@/types/analytics'
import type { DownloadResult } from '@/api/client'
import { useVisitorAnalytics, type VisitorAnalyticsScope } from '@/hooks/useVisitorAnalytics'
import {
  UniqueVisitorsStatTile, KnownVisitorsStatTile, UnknownVisitorsStatTile, ReturningVisitorsStatTile,
  SessionsStatTile, ConversionRateStatTile, AvgSessionDurationStatTile,
} from '@/components/dashboard/analytics/VisitorMetricTiles'
import {
  AvgTimeOnPageStatTile, MaxScrollDepthStatTile, CtaClicksStatTile, DownloadClicksStatTile, VideoPlaysStatTile,
} from '@/components/dashboard/analytics/EngagementMetricTiles'
import { ScrollDepthFunnelTile } from '@/components/dashboard/analytics/ScrollDepthFunnelTile'
import { TopAttributionList } from '@/components/dashboard/analytics/TopAttributionList'
import { ChannelBreakdownTile } from '@/components/dashboard/analytics/ChannelBreakdownTile'
import { AttributionViewSelect } from '@/components/dashboard/analytics/AttributionViewSelect'
import { AnalyticsExportButton } from '@/components/dashboard/analytics/AnalyticsExportButton'
import { DashboardSection } from '@/components/dashboard/analytics/DashboardSection'

/** Props shared by every band: the fetched view plus its loading/error state. */
interface BandProps {
  analytics: AnalyticsViewDto | null
  loading: boolean
  error: string | null
}

/** Range selector + (non-prod) mock-data toggle for the panel header. */
function PanelControls({ range, onRangeChange, useMock, onToggleMock, showMockToggle }: {
  range: DashboardRange
  onRangeChange: (range: DashboardRange) => void
  useMock: boolean
  onToggleMock: () => void
  showMockToggle: boolean
}) {
  return (
    <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
      {showMockToggle && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Use Mock Data</span>
          <button
            type="button"
            onClick={onToggleMock}
            aria-pressed={useMock}
            aria-label="Toggle mock data"
            className={`relative inline-flex h-5 w-9 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-slate-800 ${useMock ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white dark:bg-slate-200 shadow transform transition-transform duration-200 ${useMock ? 'translate-x-4' : ''}`}
            />
          </button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Range</label>
        <select
          value={range}
          onChange={e => onRangeChange(e.currentTarget.value as DashboardRange)}
          className="select-range transition-default"
        >
          <option value="Days30">Last 30 Days</option>
          <option value="Days60">Last 60 Days</option>
          <option value="Days90">Last 90 Days</option>
        </select>
      </div>
    </div>
  )
}

/** Visitor Engagement — richer visitor metrics (ORB-373). */
function VisitorEngagementBand({ analytics, loading, error }: BandProps) {
  return (
    <DashboardSection title="Visitor Engagement">
      <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <UniqueVisitorsStatTile metric={analytics?.UniqueVisitors ?? null} loading={loading} error={error} />
        <KnownVisitorsStatTile metric={analytics?.UniqueKnownVisitors ?? null} loading={loading} error={error} />
        <UnknownVisitorsStatTile metric={analytics?.UniqueUnknownVisitors ?? null} loading={loading} error={error} />
        <ReturningVisitorsStatTile metric={analytics?.ReturningVisitors ?? null} loading={loading} error={error} />
        <SessionsStatTile metric={analytics?.Sessions ?? null} loading={loading} error={error} />
        <ConversionRateStatTile metric={analytics?.ConversionRate ?? null} loading={loading} error={error} />
        <AvgSessionDurationStatTile metric={analytics?.AvgSessionDurationSeconds ?? null} loading={loading} error={error} />
      </div>
    </DashboardSection>
  )
}

/** Page Engagement — on-page interaction metrics + scroll-depth funnel (ORB-374 · M2). */
function PageEngagementBand({ analytics, loading, error }: BandProps) {
  return (
    <DashboardSection title="Page Engagement">
      <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        <AvgTimeOnPageStatTile metric={analytics?.Engagement?.AvgTimeOnPageSeconds ?? null} loading={loading} error={error} />
        <MaxScrollDepthStatTile metric={analytics?.Engagement?.AvgMaxScrollPct ?? null} loading={loading} error={error} />
        <CtaClicksStatTile metric={analytics?.Engagement?.CtaClicks ?? null} loading={loading} error={error} />
        <DownloadClicksStatTile metric={analytics?.Engagement?.DownloadClicks ?? null} loading={loading} error={error} />
        <VideoPlaysStatTile metric={analytics?.Engagement?.VideoPlays ?? null} loading={loading} error={error} />
        <ScrollDepthFunnelTile funnel={analytics?.Engagement?.ScrollDepthFunnel} loading={loading} error={error} />
      </div>
    </DashboardSection>
  )
}

/**
 * Attribution — where traffic came from (ORB-373). The view selector + export live in this
 * band because attribution crediting only changes these source breakdowns, and the export's
 * CSV honours the selected view.
 */
function AttributionBand({ scope, analytics, loading, error, attributionView, onAttributionViewChange, canExport, onExport, exportFilename }: BandProps & {
  scope: VisitorAnalyticsScope['scope']
  attributionView: AttributionView
  onAttributionViewChange: (view: AttributionView) => void
  canExport: boolean
  onExport: () => Promise<DownloadResult>
  exportFilename: string
}) {
  return (
    <DashboardSection
      title="Attribution"
      actions={
        <>
          <AttributionViewSelect value={attributionView} onChange={onAttributionViewChange} id={`${scope}-attribution`} hideLabel />
          {canExport && <AnalyticsExportButton onExport={onExport} fallbackFilename={exportFilename} />}
        </>
      }
    >
      <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        <TopAttributionList
          title="Top Referrers"
          icon={<Link2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
          items={analytics?.TopReferrers}
          emptyMessage="No referrer data yet."
          className="min-h-[220px]"
          loading={loading}
          error={error}
        />
        <TopAttributionList
          title="Top UTM Campaigns"
          icon={<Megaphone className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
          items={analytics?.TopUtmCampaigns}
          emptyMessage="No campaign data yet."
          className="min-h-[220px]"
          loading={loading}
          error={error}
        />
        <ChannelBreakdownTile channels={analytics?.Channels} className="min-h-[220px]" loading={loading} error={error} />
      </div>
    </DashboardSection>
  )
}

/**
 * Visitor analytics surfaced in the Tracking settings section, directly beneath the tracking
 * snippet — the richer Phase 3 metrics (visitor counts, page engagement, attribution) only
 * make sense once the snippet is installed, so they live next to it rather than on the main
 * dashboards. Scope-aware: a 'workspace' panel reports across the workspace, a 'campaign'
 * panel scopes to one campaign. State, fetching, and export live in {@link useVisitorAnalytics};
 * this component just composes the header controls and the three bands.
 */
export function VisitorAnalyticsPanel(props: VisitorAnalyticsScope) {
  const a = useVisitorAnalytics(props)

  return (
    <div className="bg-slate-50 dark:bg-slate-700/30 rounded-xl p-6 border border-slate-200 dark:border-slate-600">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Visitor Analytics</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            Visitor, engagement, and attribution metrics for traffic captured by this snippet.
          </p>
        </div>
        <PanelControls
          range={a.range}
          onRangeChange={a.setRange}
          useMock={a.useMock}
          onToggleMock={() => a.setUseMock(m => !m)}
          showMockToggle={a.showMockToggle}
        />
      </div>

      <div className="space-y-8">
        <VisitorEngagementBand analytics={a.analytics} loading={a.loading} error={a.error} />
        <PageEngagementBand analytics={a.analytics} loading={a.loading} error={a.error} />
        <AttributionBand
          scope={props.scope}
          analytics={a.analytics}
          loading={a.loading}
          error={a.error}
          attributionView={a.attributionView}
          onAttributionViewChange={a.setAttributionView}
          canExport={a.canExport}
          onExport={a.exportAnalytics}
          exportFilename={a.exportFilename}
        />
      </div>
    </div>
  )
}
